import clsx from 'clsx';
import {useFormik} from 'formik';
import {useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import * as Yup from 'yup';

import {useAuthContext} from '../../useAuthContext';
import {KeenIcon} from '@/components';
import {useLayout} from '@/providers';
import {useUser} from "@/contexts/UserContext.tsx";

const EU_COUNTRIES = [
    'Austria',
    'Belgium',
    'Bulgaria',
    'Croatia',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Estonia',
    'Finland',
    'France',
    'Germany',
    'Greece',
    'Hungary',
    'Ireland',
    'Italy',
    'Latvia',
    'Lithuania',
    'Luxembourg',
    'Malta',
    'Netherlands',
    'Poland',
    'Portugal',
    'Romania',
    'Slovakia',
    'Slovenia',
    'Spain',
    'Sweden',
    'Not EU'
];

const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    company: '',
    country: '',
    acceptTerms: false,
    acceptDataProtection: false
};

const signupSchema = Yup.object().shape({
    firstName: Yup.string()
        .min(2, 'Minimum 2 characters')
        .max(50, 'Maximum 50 characters')
        .required('First name is required'),
    lastName: Yup.string()
        .min(2, 'Minimum 2 characters')
        .max(50, 'Maximum 50 characters')
        .required('Last name is required'),
    email: Yup.string()
        .email('Wrong email format')
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Email is required'),
    password: Yup.string()
        .min(8, 'Minimum 8 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Password is required'),
    passwordConfirmation: Yup.string()
        .min(8, 'Minimum 8 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Password confirmation is required')
        .oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
    company: Yup.string()
        .min(2, 'Minimum 2 characters')
        .max(100, 'Maximum 100 characters')
        .required('Company name is required'),
    country: Yup.string().required('Country selection is required'),
    acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
    acceptDataProtection: Yup.bool().required('You must accept the terms and conditions')
});

const Signup = () => {
    const [loading, setLoading] = useState(false);
    const {register} = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || '/';
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const {currentLayout} = useLayout();
    const { refreshUser } = useUser();

    const formik = useFormik({
        initialValues,
        validationSchema: signupSchema,
        onSubmit: async (values, {setStatus, setSubmitting}) => {
            setLoading(true);
            try {
                if (!register) {
                    throw new Error('JWTProvider is required for this form.');
                }

                const success = await register(
                    values.email,
                    values.password,
                    values.passwordConfirmation,
                    values.firstName,
                    values.lastName,
                    values.company,
                    values.country,
                    values.acceptTerms,
                    values.acceptDataProtection
                );

                // Only navigate if registration was successful
                if (success) {
                    navigate(from, {replace: true});
                    await refreshUser();
                }

            } catch (error: unknown) {
                const errorMessage = (error instanceof Error) ? error.message : 'An unexpected error occurred.';
                console.error(errorMessage);
                setStatus(errorMessage);
            } finally {
                setSubmitting(false);
                setLoading(false);
            }
        }
    });

    const togglePassword = (event: { preventDefault: () => void }) => {
        event.preventDefault();
        setShowPassword(!showPassword);
    };

    const toggleConfirmPassword = (event: { preventDefault: () => void }) => {
        event.preventDefault();
        setShowConfirmPassword(!showConfirmPassword);
    };

    return (
        <div className="card max-w-[370px] w-full">
            <form
                className="card-body flex flex-col gap-5 p-10"
                noValidate
                onSubmit={formik.handleSubmit}
            >
                <div className="text-center mb-2.5">
                    <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">Sign up</h3>
                    <div className="flex items-center justify-center font-medium">
                        <span className="text-2sm text-gray-600 me-1.5">Already have an Account ?</span>
                        <Link
                            to={currentLayout?.name === 'auth-branded' ? '/auth/login' : '/auth/classic/login'}
                            className="text-2sm link"
                        >
                            Sign In
                        </Link>
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">First Name</label>
                    <label className="input">
                        <input
                            type="text"
                            placeholder="Enter First Name"
                            autoComplete="off"
                            {...formik.getFieldProps('firstName')}
                            className={clsx(
                                'form-control bg-transparent',
                                {'is-invalid': formik.touched.firstName && formik.errors.firstName},
                                {'is-valid': formik.touched.firstName && !formik.errors.firstName}
                            )}
                        />
                    </label>
                    {formik.touched.firstName && formik.errors.firstName && (
                        <span role="alert" className="text-danger text-xs mt-1">{formik.errors.firstName}</span>
                    )}
                </div>

                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">Last Name</label>
                    <label className="input">
                        <input
                            type="text"
                            placeholder="Enter Last Name"
                            autoComplete="off"
                            {...formik.getFieldProps('lastName')}
                            className={clsx(
                                'form-control bg-transparent',
                                {'is-invalid': formik.touched.lastName && formik.errors.lastName},
                                {'is-valid': formik.touched.lastName && !formik.errors.lastName}
                            )}
                        />
                    </label>
                    {formik.touched.lastName && formik.errors.lastName && (
                        <span role="alert" className="text-danger text-xs mt-1">{formik.errors.lastName}</span>
                    )}
                </div>

                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">Email</label>
                    <label className="input">
                        <input
                            placeholder="email@email.com"
                            type="email"
                            autoComplete="off"
                            {...formik.getFieldProps('email')}
                            className={clsx(
                                'form-control bg-transparent',
                                {'is-invalid': formik.touched.email && formik.errors.email},
                                {'is-valid': formik.touched.email && !formik.errors.email}
                            )}
                        />
                    </label>
                    {formik.touched.email && formik.errors.email && (
                        <span role="alert" className="text-danger text-xs mt-1">{formik.errors.email}</span>
                    )}
                </div>

                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">Company Name</label>
                    <label className="input">
                        <input
                            type="text"
                            placeholder="Your Company"
                            autoComplete="off"
                            {...formik.getFieldProps('company')}
                            className={clsx(
                                'form-control bg-transparent',
                                {'is-invalid': formik.touched.company && formik.errors.company},
                                {'is-valid': formik.touched.company && !formik.errors.company}
                            )}
                        />
                    </label>
                    {formik.touched.company && formik.errors.company && (
                        <span role="alert" className="text-danger text-xs mt-1">{formik.errors.company}</span>
                    )}
                </div>

                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">Country</label>
                    <label className="input pl-0 pr-0 border-0">
                        <select
                            id="country"
                            {...formik.getFieldProps('country')}
                            className={clsx(
                                'select bg-transparent',
                                {'is-invalid': formik.touched.country && formik.errors.country},
                                {'is-valid': formik.touched.country && !formik.errors.country}
                            )}
                            style={{
                                color: 'var(--tw-gray-600)'
                            }}
                        >
                            <option value="" disabled>Select your country</option>
                            {EU_COUNTRIES.map((countryOption) => (
                                <option key={countryOption} value={countryOption}>{countryOption}</option>
                            ))}
                        </select>
                        {formik.touched.country && formik.errors.country && (
                            <span role="alert" className="text-danger text-xs mt-1">{formik.errors.country}</span>
                        )}
                    </label>
                </div>


                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">Password</label>
                    <label className="input">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter Password"
                            autoComplete="off"
                            {...formik.getFieldProps('password')}
                            className={clsx(
                                'form-control bg-transparent',
                                {'is-invalid': formik.touched.password && formik.errors.password},
                                {'is-valid': formik.touched.password && !formik.errors.password}
                            )}
                        />
                        <button className="btn btn-icon" onClick={togglePassword}>
                            <KeenIcon icon="eye" className={clsx('text-gray-500', {hidden: showPassword})}/>
                            <KeenIcon
                                icon="eye-slash"
                                className={clsx('text-gray-500', {hidden: !showPassword})}
                            />
                        </button>
                    </label>
                    {formik.touched.password && formik.errors.password && (
                        <span role="alert" className="text-danger text-xs mt-1">{formik.errors.password}</span>
                    )}
                </div>

                <div className="flex flex-col gap-1">
                    <label className="form-label text-gray-900">Confirm Password</label>
                    <label className="input">
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Re-enter Password"
                            autoComplete="off"
                            {...formik.getFieldProps('passwordConfirmation')}
                            className={clsx(
                                'form-control bg-transparent',
                                {'is-invalid': formik.touched.passwordConfirmation && formik.errors.passwordConfirmation},
                                {'is-valid': formik.touched.passwordConfirmation && !formik.errors.passwordConfirmation}
                            )}
                        />
                        <button className="btn btn-icon" onClick={toggleConfirmPassword}>
                            <KeenIcon icon="eye" className={clsx('text-gray-500', {hidden: showConfirmPassword})}/>
                            <KeenIcon icon="eye-slash"
                                      className={clsx('text-gray-500', {hidden: !showConfirmPassword})}/>
                        </button>
                    </label>
                    {formik.touched.passwordConfirmation && formik.errors.passwordConfirmation && (
                        <span role="alert"
                              className="text-danger text-xs mt-1">{formik.errors.passwordConfirmation}</span>
                    )}
                </div>

                <label className="checkbox-group">
                    <input
                        className="checkbox checkbox-sm"
                        type="checkbox"
                        {...formik.getFieldProps('acceptTerms')}
                    />
                    <span className="checkbox-label">
            I accept{' '}
                        <Link
                            to="https://www.upflinx.com/general-terms-and-conditions-agb/"
                            className="text-2sm link"
                        >
              Terms & Conditions
            </Link>
          </span>
                </label>
                {formik.touched.acceptTerms && formik.errors.acceptTerms && (
                    <span role="alert" className="text-danger text-xs mt-1">{formik.errors.acceptTerms}</span>
                )}

                <label className="checkbox-group">
                    <input
                        className="checkbox checkbox-sm"
                        type="checkbox"
                        {...formik.getFieldProps('acceptDataProtection')}
                    />
                    <span className="checkbox-label">
            I accept{' '}
                        <Link
                            to="https://www.upflinx.com/privacy-policy-online-store/"
                            className="text-2sm link"
                        >
              Data Protection
            </Link>
          </span>
                </label>
                {formik.touched.acceptDataProtection && formik.errors.acceptDataProtection && (
                    <span role="alert" className="text-danger text-xs mt-1">{formik.errors.acceptDataProtection}</span>
                )}

                <button
                    type="submit"
                    className="btn btn-primary flex justify-center grow"
                    disabled={loading || formik.isSubmitting}
                >
                    {loading ? 'Please wait...' : 'Sign UP'}
                </button>

                {formik.status && (
                    <div className="text-danger text-xs mt-1" role="alert">
                        {formik.status}
                    </div>
                )}
            </form>
        </div>
    );
};

export {Signup};
